<template>
  <!-- 매크로 이미지: 라벨 분리 필요함 -->
  <div v-if="macro">
    <div v-if="rotate">
      <div class="rotated-wrapper">
        <img :src="labelSrc" />
        <img :src="tissueSrc" />
      </div>
    </div>
    <div v-else>
      <div class="thumbnail-wrapper">
        <picture>
          <source :srcset="labelSrc" type="image/webp">
          <source :srcset="labelSrc" type="image/jpeg">
          <img :src="labelSrc" alt="" class="image-thumbnail">
        </picture>
        <picture v-if="!onlyLabel">
          <source :srcset="tissueSrc" type="image/webp">
          <source :srcset="tissueSrc" type="image/jpeg">
          <img :src="tissueSrc" alt="" class="image-thumbnail">
        </picture>
      </div>
    </div>
  </div>
  <!-- thumb 이미지: 라벨 미포함 이미지 -->
  <div v-else>
    <picture>
      <source :srcset="webpSrc" type="image/webp">
      <source :srcset="jpegSrc" type="image/jpeg">
      <img :src="jpegSrc" alt="" class="image-thumbnail">
    </picture>
  </div>
</template>

<script>
import {combineImageUrl, splitImageUrl} from '@/utils/image-utils';
import {get} from '@/utils/store-helpers';

export default {
  name: 'ImageThumbnail',
  props: {
    image: {type: Object, default: () => {}},
    macro: {type: Boolean, default: false}, // Ignored if URL is used
    url: {type: String, default: null}, // Cannot be null if image is null or empty
    size: {type: Number},
    extraParameters: {type: Object, default: () => {}},
    rotate: {type: Boolean, default: false},
    onlyLabel: {type: Boolean, default: false},
    blindMode: {type: Boolean, default: false},
  },
  data() {
    return {
      noLabel: false,
      labelSrc: null,
      tissueSrc: null,
    };
  },
  computed: {
    serverIp: get('serverInfo/serverIp'),
    jpegSrc() {
      return this.formatSrc('jpg');
    },
    webpSrc() {
      return this.formatSrc('webp');
    },
    splitURL() {
      if (this.url === null) {
        return [];
      }

      let urlParts = splitImageUrl(this.url);
      urlParts.params.set('maxSize', this.size.toString());
      if (this.extraParameters) {
        for (const [key, value] of Object.entries(this.extraParameters)) {
          urlParts.params.set(key, value.toString());
        }
      }
      return urlParts;
    }
  },
  watch: {
    // rotate, size, jpegSrc 중 하나라도 변경되면 이미지를 다시 처리합니다.
    rotate: 'devideImage',
    size: 'devideImage',
    jpegSrc: 'devideImage'
  },
  mounted() {
    this.devideImage();
  },
  methods: {
    replaceLocalhostWithServerIp(str, serverIp) {
      if (!str)
        return '';
      return str.replace(/127\.0\.0\.1/g, serverIp);
    },
    combineURL({host, pathname, format, params}) {
      return combineImageUrl({host, pathname, format, params});
    },
    adjustProtocol(url) {
      const protocol = window.location.protocol;
      return url.replace(/^https?:/, protocol);
    },
    formatSrc(format) {
      let thumbUrl;
      if (this.url !== null) {
        thumbUrl = this.combineURL({format, ...this.splitURL}); 
        thumbUrl = this.replaceLocalhostWithServerIp(thumbUrl, this.serverIp);
        thumbUrl = this.adjustProtocol(thumbUrl);
        return thumbUrl;
      }
      if (this.macro) {
        if (this.image.macroURL) {
          thumbUrl = this.image.associatedImageURL('macro', this.size, format, this.extraParameters); 
          thumbUrl = this.replaceLocalhostWithServerIp(thumbUrl, this.serverIp);
          thumbUrl = this.adjustProtocol(thumbUrl);
          return thumbUrl;
        }
        else {
          this.noLabel = true;
          thumbUrl = this.image.thumbURL(this.size, format, this.extraParameters);
          thumbUrl = this.replaceLocalhostWithServerIp(thumbUrl, this.serverIp);
          thumbUrl = this.adjustProtocol(thumbUrl);
          return thumbUrl;
        }
      }
      thumbUrl = this.image.thumbURL(this.size, format, this.extraParameters);
      thumbUrl = this.replaceLocalhostWithServerIp(thumbUrl, this.serverIp);
      thumbUrl = this.adjustProtocol(thumbUrl);
      return thumbUrl;
    },
    async devideImage() {
      if (!this.macro) {
        this.labelSrc = null;
        this.tissueSrc = null;
        return;
      }

      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = this.jpegSrc;

      await new Promise(resolve => (img.onload = resolve));

      const W = img.width;
      const H = img.height;

      // 공통 사이즈 계산
      const labelWidth = W;
      const labelHeight = W;
      // const labelHeight = Math.floor(W * 0.8);

      const hasLabel = !this.noLabel;

      const tissueSrcX = 0;
      const tissueSrcY = hasLabel ? labelHeight : 0;
      const tissueSrcW = W;
      const tissueSrcH = hasLabel ? (H - labelHeight) : H;

      // 1) 라벨 캔버스
      const leftCanvas = document.createElement('canvas');
      leftCanvas.width = labelWidth;
      leftCanvas.height = labelHeight;

      const leftCtx = leftCanvas.getContext('2d');

      if (!hasLabel || this.blindMode) {
        const fontSize = Math.floor(labelHeight * 0.2);

        leftCtx.fillStyle = '#e1e1e1';
        leftCtx.fillRect(0, 0, labelWidth, labelHeight);

        leftCtx.strokeStyle = '#b5b5b5';
        leftCtx.strokeRect(0, 0, labelWidth, labelHeight);

        leftCtx.fillStyle = '#666';
        leftCtx.font = `bold ${fontSize}px sans-serif`;
        leftCtx.textAlign = 'center';
        leftCtx.textBaseline = 'middle';

        const text = this.blindMode ? 'Hidden' : 'No Label';
        leftCtx.fillText(text, labelWidth / 2, labelHeight / 2);
      } 
      else {
        leftCtx.drawImage(
          img,
          0, 0,
          labelWidth, labelHeight,
          0, 0,
          labelWidth, labelHeight
        );
      }

      // 2) 조직 캔버스
      const rightCanvas = document.createElement('canvas');
      const rightCtx = rightCanvas.getContext('2d');

      if (this.rotate) {
        rightCanvas.width = tissueSrcH;
        rightCanvas.height = tissueSrcW;

        rightCtx.translate(0, rightCanvas.height);
        rightCtx.rotate(-Math.PI / 2);
      } 
      else {
        rightCanvas.width = tissueSrcW;
        rightCanvas.height = tissueSrcH;
      }

      rightCtx.drawImage(
        img,
        tissueSrcX, tissueSrcY,
        tissueSrcW, tissueSrcH,
        0, 0,
        tissueSrcW, tissueSrcH
      );

      // 3) 결과 저장
      this.labelSrc = leftCanvas.toDataURL('image/jpeg');
      this.tissueSrc = rightCanvas.toDataURL('image/jpeg');
    }
  }
};
</script>

<style scoped>
.rotated-wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 50px;
}

.thumbnail-wrapper {
  display: flex;
  flex-direction: column;
}

.image-thumbnail {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

/* .label-area {
  background-color: rgb(225, 225, 225);
  height: 70px;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 13px;
  font-weight: 500;
  color: #666;

  letter-spacing: 0.02em;
  user-select: none;

  border-bottom: 1px solid #d0d0d0;
} */
</style>
